/**
 * Aba Ajuda: perguntas e respostas sobre o LFRCORD inteiro.
 *
 * A versão e o botão de atualizar saíram daqui para uma aba própria. As duas
 * coisas nunca se pareceram: atualizar é uma AÇÃO eventual, e isto aqui é
 * leitura para quando alguma coisa não funciona.
 *
 * O conteúdo saiu de uma varredura do app, não de imaginação: as mensagens de
 * recusa do servidor e das rotas, os limites de `shared/protocol.ts`, as oito
 * permissões, e as regras que o código aplica em silêncio (quem pode mandar
 * direta, quando o aviso toca, quando o status vira ausente sozinho). O
 * critério de entrada é um só: **cada resposta descreve comportamento que
 * existe de verdade.** Conselho genérico de suporte fica de fora — uma FAQ que
 * descreve um app imaginário faz a pessoa duvidar da tela em vez de duvidar do
 * texto, e aí a ajuda inteira perde valor.
 *
 * Onde app e navegador se comportam diferente, o item leva etiqueta dizendo
 * qual dos dois. Mandar alguém procurar um botão que não existe no lugar onde
 * ela está é a forma mais rápida de queimar a confiança na ajuda.
 */
import { useMemo, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import {
  MAX_BIO_LENGTH,
  MAX_MESSAGE_LENGTH,
  MAX_NAME_LENGTH,
  MAX_STATUS_LENGTH,
  MAX_UPLOAD_BYTES,
  MIN_PASSWORD_LENGTH,
  VERIFICATION_CODE_LENGTH,
} from "@shared/protocol";
import { C, FONT_MONO } from "@/theme";

const ROTULO: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
  textTransform: "uppercase",
};

const CARTAO: CSSProperties = {
  padding: "14px 16px",
  border: `1px solid ${C.lineSoft}`,
  borderRadius: 12,
  background: C.field,
};

const TEXTO: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: C.textSoft,
  margin: 0,
};

const CAMPO: CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 9,
  background: C.ink,
  color: C.textStrong,
  fontSize: 13,
  outline: "none",
};

/** Destaque curto dentro de uma resposta — nome de botão, aba, mensagem. */
function Grifo({ children }: { children: ReactNode }): ReactElement {
  return <strong style={{ color: C.textStrong, fontWeight: 600 }}>{children}</strong>;
}

/** Mensagem literal que o app mostra. Monoespaçada para bater com o que se leu na tela. */
function Msg({ children }: { children: ReactNode }): ReactElement {
  return (
    <span
      style={{
        fontFamily: FONT_MONO,
        fontSize: 11.5,
        color: C.textStrong,
        background: C.ink,
        border: `1px solid ${C.lineSoft}`,
        borderRadius: 5,
        padding: "1px 5px",
      }}
    >
      {children}
    </span>
  );
}

type Pergunta = {
  /** Onde a resposta vale. Ausente = vale nos dois. */
  onde?: "app" | "navegador";
  pergunta: string;
  resposta: ReactNode;
  /** Palavras que a busca considera além da própria pergunta. */
  busca?: string;
};

type Grupo = { titulo: string; itens: Pergunta[] };

/* ---------------------------------------------------------------------- */
/* Voz                                                                     */
/* ---------------------------------------------------------------------- */

const VOZ: Pergunta[] = [
  {
    pergunta: "No meio da call as pessoas pararam de me ouvir",
    busca: "microfone mudo silencio audio sumiu ninguem ouve",
    resposta: (
      <>
        O microfone passa por um processamento de áudio, e o Windows às vezes derruba esse
        processamento sozinho — ao trocar de fone, religar um dispositivo, ou quando o serviço
        de áudio do sistema se reinicia. Quando isso acontece o microfone continua
        &ldquo;ligado&rdquo; na tela e apenas para de mandar som, e é por isso que ninguém
        percebe. O app vigia isso e refaz o microfone sozinho, mostrando{" "}
        <Msg>O microfone parou de responder. Reabrindo…</Msg>. Se você viu esse aviso, era isso
        e já se resolveu. Se o som sumiu <em>sem</em> ele, é outra causa — avise a staff
        dizendo exatamente isso, porque essa distinção é metade do diagnóstico.
      </>
    ),
  },
  {
    pergunta: "Entrei numa call e caí da que eu estava",
    busca: "duas calls sala voz derrubou",
    resposta: (
      <>
        É proposital. Uma conta fica em uma sala de voz por vez, mesmo em outro servidor e mesmo
        que a outra conexão seja de outro aparelho. Sem isso a mesma pessoa aparecia duas vezes
        na lista e uma das duas nunca respondia. Vale como um fone: o áudio vai para onde você
        acabou de sentar.
      </>
    ),
  },
  {
    pergunta: "Não consigo entrar na sala de voz",
    busca: "conectar voz permissao nao entra",
    resposta: (
      <>
        Se aparece <Msg>Conectar em voz</Msg> negado, é cargo: fale com quem administra. Se
        aparece <Msg>Conexão de voz bloqueada pela moderação.</Msg>, você está silenciado ou em
        timeout. Se não aparece nada e simplesmente não conecta, costuma ser a rede — algumas
        redes de celular e quase toda rede corporativa bloqueiam conexão direta entre
        computadores. Teste em outra rede antes de abrir chamado: se funcionar, o problema é a
        rede.
      </>
    ),
  },
  {
    pergunta: "Estou mudo ou surdo e o botão não obedece",
    busca: "silenciado ensurdecido moderacao timeout",
    resposta: (
      <>
        Além do seu botão, um moderador pode silenciar ou ensurdecer alguém — e aí o cadeado é
        do servidor, não seu. Timeout tem o mesmo efeito e bloqueia o texto junto, com prazo
        marcado. Nos três casos a saída é falar com a staff; não há como desfazer do seu lado.
      </>
    ),
  },
  {
    pergunta: "Como funciona o apertar para falar",
    busca: "ptt push to talk barra espaco",
    resposta: (
      <>
        A tecla é a <Grifo>barra de espaço</Grifo>, e ela só vale enquanto você está conectado
        numa sala de voz. Com o modo ligado, o microfone fica fechado e abre enquanto a barra
        estiver pressionada. Você troca entre <Grifo>Sempre aberto</Grifo> e{" "}
        <Grifo>Apertar para falar</Grifo> nos controles da sala.
      </>
    ),
  },
  {
    pergunta: "Quero abaixar o volume de uma pessoa só",
    busca: "volume individual alto baixo",
    resposta: (
      <>
        Passe o mouse sobre o tile da pessoa na sala de voz: aparece um controle de volume só
        dela. O ajuste é seu e local — ninguém mais é afetado, e ele vale também para o áudio da
        transmissão dessa pessoa.
      </>
    ),
  },
  {
    pergunta: "O som está saindo no dispositivo errado",
    busca: "saida fone caixa dispositivo audio entrada",
    resposta: (
      <>
        Nos controles da sala de voz dá para escolher entrada (microfone) e saída (fone). Os
        nomes dos dispositivos só aparecem depois que o navegador libera o microfone — antes
        disso a lista vem sem rótulo, e é normal. Trocar de entrada reabre o microfone
        automaticamente.
      </>
    ),
  },
  {
    pergunta: "A supressão de ruído está deixando minha voz estranha",
    busca: "ruido krisp gtcrn voz robotica cpu",
    resposta: (
      <>
        A supressão por IA roda no seu computador e pode gerar artefato em algumas vozes, além
        de custar CPU. Ela é desligável nas configurações — e mesmo desligada continua valendo a
        supressão nativa do navegador, que é mais leve. Desligar exige reabrir o microfone, o
        que o app faz sozinho.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Transmissão                                                             */
/* ---------------------------------------------------------------------- */

const TRANSMISSAO: Pergunta[] = [
  {
    pergunta: "Quero ver o que estou transmitindo",
    busca: "previa propria tela espelho",
    resposta: (
      <>
        Sua tela aparece normalmente no tile da transmissão. Se você compartilha a tela inteira,
        o LFRCORD aparece dentro de si mesmo, repetido — não quebra nada, mas cansa de olhar. O
        botão <Grifo>Ocultar prévia</Grifo> no canto do tile resolve, e o resto da sala continua
        vendo normalmente.
      </>
    ),
  },
  {
    pergunta: "Como assisto a transmissão de outra pessoa",
    busca: "assistir janela tela cheia",
    resposta: (
      <>
        Clique na tela dela e ela abre em <Grifo>uma janela separada</Grifo>, que vai para o
        outro monitor ou encosta ao lado. É para você continuar lendo o canal e falando enquanto
        assiste. O som continua saindo pela janela principal, junto com o volume por pessoa.
      </>
    ),
  },
  {
    onde: "navegador",
    pergunta: "Incluí o áudio do computador e todo mundo ouviu eco",
    busca: "eco audio sistema retorno microfonia",
    resposta: (
      <>
        Pelo navegador, capturar o áudio do sistema pega <em>tudo</em> que toca no computador —
        inclusive a voz das pessoas da call, que volta para elas como eco. O navegador não sabe
        separar isso. O app de computador captura excluindo o próprio LFRCORD, então lá não
        acontece. Para transmitir com som, use o app.
      </>
    ),
  },
  {
    pergunta: "Minha transmissão está travando ou embaçada",
    busca: "qualidade fps nitidez movimento lag",
    resposta: (
      <>
        Dois controles mudam o resultado. A <Grifo>qualidade</Grifo> vai de 480p30 a 4K60 — a
        opção <Grifo>Automática</Grifo> se adapta sozinha, e baixar ajuda quando a internet de
        subida é o gargalo. O modo escolhe o que sacrificar:{" "}
        <Grifo>Movimento</Grifo> prioriza quadros por segundo (jogo) e{" "}
        <Grifo>Nitidez</Grifo> prioriza definição (texto e código). Jogo em Nitidez engasga
        mesmo.
      </>
    ),
  },
  {
    pergunta: "Parei de transmitir pela barra do navegador e o app não percebeu",
    busca: "parar transmissao encerrou sozinho",
    resposta: (
      <>
        Percebe, sim: o app escuta o fim da captura e desliga a transmissão sozinho, tirando o
        tile da sala. Se o tile ficou preso, a transmissão já não está indo — sair e voltar da
        sala limpa o estado.
      </>
    ),
  },
  {
    pergunta: "Quem pode transmitir",
    busca: "permissao tela compartilhar",
    resposta: (
      <>
        Quem tiver a permissão <Grifo>Compartilhar tela</Grifo> em algum cargo no servidor. A
        permissão é a união de todos os seus cargos: basta um deles ter. Sem ela, o botão nem
        aparece.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Mensagens                                                               */
/* ---------------------------------------------------------------------- */

const MENSAGENS: Pergunta[] = [
  {
    pergunta: "Apareceu “Devagar com as mensagens”",
    busca: "rate limit spam flood devagar",
    resposta: (
      <>
        É o limite de velocidade de envio, para o canal não virar enxurrada. Espere alguns
        segundos e mande de novo — nada foi perdido. O primo mais severo dele é{" "}
        <Msg>Você mandou eventos demais e foi desconectado. Recarregue a página.</Msg>, que
        acontece quando o app manda ações demais em rajada; recarregar resolve.
      </>
    ),
  },
  {
    pergunta: "Não consigo apagar ou editar uma mensagem",
    busca: "editar apagar mensagem permissao",
    resposta: (
      <>
        Editar é só do autor — <Msg>Só o autor pode editar a própria mensagem.</Msg> não tem
        exceção, nem para quem administra. Apagar a dos outros exige a permissão{" "}
        <Grifo>Expulsar membros</Grifo>. A sua própria você apaga sempre.
      </>
    ),
  },
  {
    pergunta: "Mandei @todos e não notificou ninguém",
    busca: "todos everyone mencao",
    resposta: (
      <>
        Mencionar todo mundo é a permissão <Grifo>Mencionar @todos</Grifo>, separada das outras.
        Sem ela o texto sai como texto comum, sem virar aviso para ninguém.
      </>
    ),
  },
  {
    pergunta: "Uma pessoa sumiu do canal para mim",
    busca: "bloquear ignorar sumiu mensagens",
    resposta: (
      <>
        Você bloqueou ou ignorou essa pessoa. As mensagens dela deixam de aparecer para você em
        canal e em direta — continuam existindo para os outros. Dá para desfazer no menu do
        nome dela.
      </>
    ),
  },
  {
    pergunta: "O histórico antigo não carrega",
    busca: "historico rolar antigas paginacao",
    resposta: (
      <>
        O histórico vem por páginas conforme você rola para cima. Sala de voz não tem histórico,
        e canal que já chegou ao começo para de pedir. Se ficou preso carregando, trocar de
        canal e voltar refaz o pedido.
      </>
    ),
  },
  {
    pergunta: "A busca não acha o que eu sei que existe",
    busca: "buscar procurar mensagem",
    resposta: (
      <>
        A busca devolve no máximo 60 resultados e cobre as conversas às quais você tem acesso.
        Termo muito curto ou muito comum enche o teto rápido — acrescente uma palavra rara da
        mensagem em vez de encurtar.
      </>
    ),
  },
  {
    pergunta: "Meu anexo não subiu",
    busca: "anexo arquivo upload tamanho imagem",
    resposta: (
      <>
        O limite é <Grifo>100 MB</Grifo> por arquivo, e <Grifo>8 MB</Grifo> para imagem de
        perfil ou do servidor. Avatar aceita PNG, JPEG, WEBP, GIF ou AVIF. Arquivo vazio é
        recusado. Se aparecer <Msg>Entre na sua conta para enviar arquivos.</Msg>, sua sessão
        caiu — recarregue e entre de novo.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Conta                                                                   */
/* ---------------------------------------------------------------------- */

const CONTA: Pergunta[] = [
  {
    pergunta: "Entrei em outro lugar e fui deslogado aqui",
    busca: "sessao unica logout app site",
    resposta: (
      <>
        É proposital: uma conta fica logada em um lugar por vez. Entrar no app desconecta o
        site, e vice-versa. O aparelho que cai mostra{" "}
        <Msg>Sua conta entrou em outro lugar.</Msg> Se isso aconteceu sem você ter entrado em
        lugar nenhum, troque a senha e confira <Grifo>Sessões ativas</Grifo> — aí não é o
        recurso, é outra pessoa na sua conta.
      </>
    ),
  },
  {
    pergunta: "Me cadastrei e o código não chegou no e-mail",
    busca: "codigo verificacao cadastro email",
    resposta: (
      <>
        Quando o envio falha, a tela para de prometer e-mail e pede o código de{" "}
        {VERIFICATION_CODE_LENGTH} dígitos a alguém da crew — se você viu essa mensagem, o
        cadastro ficou guardado e a staff consegue te passar. O código vale{" "}
        <Grifo>15 minutos</Grifo> e aceita <Grifo>5 tentativas</Grifo>; estourou qualquer um dos
        dois, é refazer o cadastro. Antes de pedir, confira o spam.
      </>
    ),
  },
  {
    pergunta: "Esqueci a senha",
    busca: "senha esqueci recuperar trocar",
    resposta: (
      <>
        Não há recuperação por e-mail. Quem é fundador da plataforma troca a senha de uma conta
        pelo painel, e a troca encerra todas as sessões daquela conta. Peça à staff. Se você
        entrou pelo Google, a conta não tem senha — continue entrando pelo Google.
      </>
    ),
  },
  {
    pergunta: "Meu status virou ausente sozinho",
    busca: "ausente away status invisivel ocupado",
    resposta: (
      <>
        Cinco minutos sem nenhuma interação e o status vira <Grifo>ausente</Grifo> sozinho;
        qualquer atividade devolve para online. Se você escolheu um status à mão, ele manda — o
        automático só mexe no que o app mesmo decidiu. <Grifo>Invisível</Grifo> aparece como
        offline para todo mundo, e o servidor honra isso no próprio evento: nem quem inspeciona
        a conexão vê o estado real.
      </>
    ),
  },
  {
    pergunta: "Quero deslogar de todos os aparelhos",
    busca: "sessoes ativas encerrar seguranca",
    resposta: (
      <>
        Em <Grifo>Sessões ativas</Grifo> há a lista com aparelho, IP e data, e o botão de
        encerrar as outras. O aparelho encerrado cai na hora, não na próxima vez que abrir.
        Sessão dura 30 dias sem uso.
      </>
    ),
  },
  {
    pergunta: "Fui banido — e agora",
    busca: "banido suspensao expulso",
    resposta: (
      <>
        O aviso mostra o motivo e quem baniu, para você saber a quem recorrer. Banimento pode
        ser permanente ou uma suspensão com data, e a suspensão expira sozinha. Conta banida não
        entra, e o cadastro com o mesmo e-mail ou usuário também é recusado.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Amigos e diretas                                                        */
/* ---------------------------------------------------------------------- */

const AMIGOS: Pergunta[] = [
  {
    pergunta: "Não consigo mandar mensagem direta para alguém",
    busca: "dm direta amizade privado",
    resposta: (
      <>
        A direta abre em três casos: vocês são amigos, vocês já conversaram antes, ou vocês
        compartilham um servidor — como no Discord. Fora disso, mande a solicitação de amizade
        primeiro.
      </>
    ),
  },
  {
    pergunta: "Adicionei alguém e nada aconteceu",
    busca: "solicitacao amizade pendente aceitar",
    resposta: (
      <>
        A solicitação fica pendente até a outra pessoa aceitar. Enviar de novo devolve{" "}
        <Msg>Você já enviou uma solicitação.</Msg> — não é erro, é a mesma solicitação ainda
        esperando. Você não pode adicionar a si mesmo.
      </>
    ),
  },
  {
    pergunta: "Qual a diferença entre bloquear e ignorar",
    busca: "bloquear ignorar diferenca",
    resposta: (
      <>
        Os dois somem com as mensagens da pessoa da <em>sua</em> tela, em canal e em direta. A
        mensagem continua existindo para os outros. Use quando alguém está incomodando e você
        não quer envolver moderação; quando quiser que pare para todo mundo, chame a staff.
      </>
    ),
  },
  {
    pergunta: "Não achei a pessoa pelo nome de usuário",
    busca: "handle arroba procurar pessoa",
    resposta: (
      <>
        A busca de pessoa é pelo <Grifo>@handle</Grifo> exato, sem o arroba, tudo minúsculo —{" "}
        <Msg>Não encontramos ninguém com esse nome de usuário.</Msg> quase sempre é
        maiúscula, espaço ou o nome de exibição no lugar do handle. Handle vai de 3 a 24
        caracteres e aceita letras minúsculas, números, ponto, hífen e underline.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Cargos                                                                  */
/* ---------------------------------------------------------------------- */

const CARGOS: Pergunta[] = [
  {
    pergunta: "Recebi um cargo e não mudou nada na lista de membros",
    busca: "cargo nao aparece lista membros",
    resposta: (
      <>
        A lista da direita agrupa cada pessoa pelo <em>cargo mais alto</em> dela, e a cor do
        nome vem do mesmo lugar. Cargo novo abaixo de um que você já tem não muda nem o
        agrupamento nem a cor — mas está valendo. Para ver todos os cargos de alguém,{" "}
        <Grifo>abra o perfil</Grifo> clicando no nome: eles aparecem lá e mudam na hora quando
        alguém dá ou tira.
      </>
    ),
  },
  {
    pergunta: "Dei um cargo e o servidor recusou",
    busca: "hierarquia nivel acima cargo recusado",
    resposta: (
      <>
        Vale hierarquia por posição, como no Discord: ter a permissão não basta. Você não
        atribui cargo acima do seu nível, nem mexe em quem está acima de você. Dono do servidor
        e fundador da plataforma passam por cima disso. O cargo de fundador nunca é atribuído
        nem apagado.
      </>
    ),
  },
  {
    pergunta: "Tenho dois cargos — qual permissão vale",
    busca: "uniao permissoes varios cargos",
    resposta: (
      <>
        A <Grifo>união</Grifo> de todos eles: um cargo nunca anula o outro. Basta um dos seus
        cargos ter a permissão para você tê-la. O cargo mais alto decide só a aparência (cor do
        nome e agrupamento), nunca o que você pode fazer.
      </>
    ),
  },
  {
    pergunta: "Quais são as permissões e o que cada uma faz",
    busca: "lista permissoes canais cargos expulsar silenciar",
    resposta: (
      <>
        São oito: <Grifo>Gerenciar canais</Grifo> (criar, editar e remover),{" "}
        <Grifo>Gerenciar cargos</Grifo>, <Grifo>Expulsar membros</Grifo> (que também autoriza
        apagar mensagem alheia), <Grifo>Silenciar membros</Grifo> (silenciar, ensurdecer, mover
        e desconectar da voz), <Grifo>Enviar mensagens</Grifo>,{" "}
        <Grifo>Conectar em voz</Grifo>, <Grifo>Compartilhar tela</Grifo> e{" "}
        <Grifo>Mencionar @todos</Grifo>.
      </>
    ),
  },
  {
    pergunta: "Apaguei um cargo sem querer",
    busca: "apagar cargo desfazer restaurar",
    resposta: (
      <>
        Salvar a lista de cargos <em>apaga</em> todo cargo que não estiver nela, e não há
        desfazer. O app se protege de um caso só: nunca deixa o servidor sem ninguém capaz de
        editar cargos — se a última permissão de cargos sumiria, ela volta para o seu.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Servidores                                                              */
/* ---------------------------------------------------------------------- */

const SERVIDORES: Pergunta[] = [
  {
    pergunta: "Não consigo entrar por um convite",
    busca: "convite codigo invite expirado",
    resposta: (
      <>
        <Msg>Convite não encontrado.</Msg> cobre três casos que a tela não distingue de
        propósito: código errado, convite revogado, e convite que estourou o limite de usos ou o
        prazo. Peça um novo a quem te chamou. Se aparecer{" "}
        <Msg>Os convites estao temporariamente desativados.</Msg>, é um interruptor de
        emergência da plataforma — aí é esperar.
      </>
    ),
  },
  {
    pergunta: "Não consigo criar servidor",
    busca: "criar servidor bloqueado",
    resposta: (
      <>
        <Msg>A criação de servidores está temporariamente desativada.</Msg> é um interruptor de
        emergência ligado por um fundador da plataforma; vale para todo mundo menos para eles.
        O nome do servidor vai até 40 caracteres e não pode ficar vazio.
      </>
    ),
  },
  {
    pergunta: "Quero sair de um servidor que eu criei",
    busca: "sair servidor dono transferir apagar",
    resposta: (
      <>
        O dono não sai do próprio servidor: <Msg>Apague-o ou transfira antes.</Msg> Apagar é
        definitivo e leva canais, mensagens e membros junto.
      </>
    ),
  },
  {
    pergunta: "Não consigo criar ou apagar um canal",
    busca: "canal criar apagar nome repetido",
    resposta: (
      <>
        Criar e apagar exigem <Grifo>Gerenciar canais</Grifo>. Nome de canal vai até 24
        caracteres e o tópico até 120, e nomes se repetindo no mesmo servidor são recusados. O
        servidor sempre precisa sobrar com pelo menos um canal de texto — o último não é
        apagável.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* Avisos                                                                  */
/* ---------------------------------------------------------------------- */

const AVISOS: Pergunta[] = [
  {
    pergunta: "Não recebo notificação de mensagem",
    busca: "notificacao aviso som mencao dm",
    resposta: (
      <>
        O aviso só sai em dois casos — <Grifo>direta</Grifo> ou{" "}
        <Grifo>menção a você</Grifo> — e só com a janela <Grifo>fora de foco</Grifo>. Mensagem
        comum de canal, com a janela aberta, nunca notifica. O som toca mesmo sem permissão do
        navegador; a janelinha do sistema depende dela, que se concede nas configurações.
      </>
    ),
  },
  {
    pergunta: "Apareceu um aviso da equipe do LFRCORD",
    busca: "aviso plataforma anuncio staff",
    resposta: (
      <>
        Fundadores da plataforma podem mandar aviso para um servidor ou para todos. É
        informativo e não pede ação. Se ele diz que um servidor foi removido, foi decisão de
        moderação da plataforma.
      </>
    ),
  },
  {
    pergunta: "O app diz que está em manutenção",
    busca: "manutencao fora do ar interruptor",
    resposta: (
      <>
        <Msg>O LFRCORD está em manutenção. Tente de novo em instantes.</Msg> é um interruptor
        de emergência: só fundadores da plataforma entram enquanto estiver ligado. Não é queda —
        é decisão, e costuma durar pouco.
      </>
    ),
  },
];

/* ---------------------------------------------------------------------- */
/* App                                                                     */
/* ---------------------------------------------------------------------- */

const APP: Pergunta[] = [
  {
    onde: "app",
    pergunta: "Fechei no X e o LFRCORD sumiu da barra de tarefas",
    busca: "bandeja tray fechar minimizar",
    resposta: (
      <>
        Ele não fechou: foi para a <Grifo>bandeja</Grifo>, ao lado do relógio. É de propósito —
        assim você continua na chamada e recebendo mensagem depois de fechar a janela sem
        querer. Clique no ícone para voltar; botão direito e <Grifo>Sair do LFRCORD</Grifo> sai
        de verdade.
      </>
    ),
  },
  {
    onde: "app",
    pergunta: "Cliquei em atualizar, o app fechou e não voltou",
    busca: "atualizacao nao reabre update",
    resposta: (
      <>
        Era um defeito e foi corrigido — o app novo podia subir antes de o antigo terminar de
        sair, e se encerrava achando que era uma segunda janela. Se acontecer de novo, abrir
        pelo atalho funciona e nada se perde: a versão nova já está instalada.
      </>
    ),
  },
  {
    onde: "app",
    pergunta: "Por que usar o app em vez do navegador",
    busca: "app desktop vantagem diferenca navegador",
    resposta: (
      <>
        Três coisas só existem no app: transmissão em <Grifo>1080p60</Grifo> e acima, captura do{" "}
        <Grifo>áudio do sistema sem eco</Grifo> (excluindo o próprio LFRCORD), e o seletor de
        janela próprio. Some a isso ficar na bandeja e atualizar sozinho.
      </>
    ),
  },
  {
    onde: "app",
    pergunta: "Onde vejo minha versão",
    busca: "versao atualizar checar",
    resposta: (
      <>
        Na aba <Grifo>Atualização</Grifo>, aqui do lado: versão instalada, mais recente
        publicada e o botão de procurar agora sem esperar a próxima abertura.
      </>
    ),
  },
  {
    onde: "app",
    pergunta: "O login com Google abriu meu navegador",
    busca: "google login navegador externo",
    resposta: (
      <>
        É obrigatório: o Google recusa autenticar dentro de janela embutida. O app abre o fluxo
        no navegador do sistema e recebe a volta por um código de uso único que vale um minuto —
        a sessão em si nunca trafega no endereço.
      </>
    ),
  },
];

const GRUPOS: Grupo[] = [
  { titulo: "Voz e microfone", itens: VOZ },
  { titulo: "Transmissão de tela", itens: TRANSMISSAO },
  { titulo: "Mensagens, anexos e busca", itens: MENSAGENS },
  { titulo: "Conta, login e sessões", itens: CONTA },
  { titulo: "Amigos e conversas diretas", itens: AMIGOS },
  { titulo: "Cargos e permissões", itens: CARGOS },
  { titulo: "Servidores, canais e convites", itens: SERVIDORES },
  { titulo: "Avisos e notificações", itens: AVISOS },
  { titulo: "App de computador", itens: APP },
];

/**
 * Uma pergunta, fechada por padrão.
 *
 * Tudo aberto vira paredão que ninguém lê; fechado com título bom é uma lista
 * que se varre em segundos. O título fica na voz de quem TEM o problema ("as
 * pessoas pararam de me ouvir"), não na de quem escreveu o código ("falha no
 * contexto de áudio") — quem procura ajuda não sabe o nome técnico do que está
 * vivendo.
 */
function Item({ item, abertaPorBusca }: { item: Pergunta; abertaPorBusca: boolean }): ReactElement {
  const [aberta, setAberta] = useState(false);
  const mostrar = aberta || abertaPorBusca;
  return (
    <div style={{ borderTop: `1px solid ${C.lineSoft}` }}>
      <button
        type="button"
        onClick={() => setAberta(atual => !atual)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 2px",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          aria-hidden
          style={{ width: 14, flex: "0 0 auto", color: C.textFaint, fontFamily: FONT_MONO, fontSize: 12 }}
        >
          {mostrar ? "−" : "+"}
        </span>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: C.textStrong }}>
          {item.pergunta}
        </span>
        {item.onde ? (
          <span style={{ ...ROTULO, flex: "0 0 auto" }}>
            {item.onde === "app" ? "no app" : "no navegador"}
          </span>
        ) : null}
      </button>
      {mostrar ? (
        <div style={{ padding: "0 2px 14px 24px" }}>
          <p style={TEXTO}>{item.resposta}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Normaliza para a busca casar sem depender de acento nem de maiúscula. */
function chave(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function Ajuda(): ReactElement {
  const [busca, setBusca] = useState("");
  const termo = chave(busca.trim());

  /*
    Com quase cinquenta perguntas, rolar até achar deixou de ser viável — e uma
    lista longa demais para varrer é o mesmo que uma lista sem a resposta.
    A busca casa a pergunta e as palavras-chave de cada item; o campo `busca`
    existe porque ninguém digita "supressão de ruído", digita "voz robótica".
  */
  const filtrados = useMemo(() => {
    if (termo.length === 0) return GRUPOS;
    return GRUPOS.map(grupo => ({
      titulo: grupo.titulo,
      itens: grupo.itens.filter(item =>
        chave(`${item.pergunta} ${item.busca ?? ""} ${grupo.titulo}`).includes(termo),
      ),
    })).filter(grupo => grupo.itens.length > 0);
  }, [termo]);

  const total = GRUPOS.reduce((soma, grupo) => soma + grupo.itens.length, 0);
  const achados = filtrados.reduce((soma, grupo) => soma + grupo.itens.length, 0);

  return (
    <div style={{ maxWidth: 560, display: "grid", gap: 18 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <input
          value={busca}
          onChange={event => setBusca(event.target.value)}
          placeholder={`Procurar entre ${total} perguntas — ex.: "eco", "cargo", "bandeja"`}
          style={CAMPO}
        />
        <p style={{ ...TEXTO, color: C.textDim, fontSize: 12 }}>
          {termo.length > 0
            ? `${achados} de ${total} perguntas`
            : "Clique numa pergunta para abrir a resposta."}
        </p>
      </div>

      {filtrados.map(grupo => (
        <section key={grupo.titulo} style={CARTAO}>
          <div style={{ ...ROTULO, marginBottom: 4 }}>{grupo.titulo}</div>
          {grupo.itens.map(item => (
            <Item key={item.pergunta} item={item} abertaPorBusca={termo.length > 0} />
          ))}
        </section>
      ))}

      {achados === 0 ? (
        <section style={CARTAO}>
          <p style={TEXTO}>
            Nada com esse termo. Tente uma palavra do que você viu na tela — a busca cobre também
            apelidos comuns (&ldquo;eco&rdquo;, &ldquo;lag&rdquo;, &ldquo;tray&rdquo;).
          </p>
        </section>
      ) : null}

      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Limites, de uma olhada</div>
        <ul style={{ ...TEXTO, margin: 0, paddingLeft: 18 }}>
          <li>Mensagem: {MAX_MESSAGE_LENGTH} caracteres</li>
          <li>Anexo: {Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB · imagem de perfil: 8 MB</li>
          <li>
            Nome: {MAX_NAME_LENGTH} · bio: {MAX_BIO_LENGTH} · status: {MAX_STATUS_LENGTH}{" "}
            caracteres
          </li>
          <li>Canal: 24 caracteres · tópico: 120 · servidor: 40</li>
          <li>Senha: mínimo {MIN_PASSWORD_LENGTH} · sessão: 30 dias sem uso</li>
          <li>Código de cadastro: {VERIFICATION_CODE_LENGTH} dígitos, 15 minutos, 5 tentativas</li>
          <li>Busca: até 60 resultados · ausente automático: 5 minutos</li>
        </ul>
      </section>

      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Não achou aqui?</div>
        <p style={TEXTO}>
          Fale com a staff em qualquer canal do servidor. Se o problema for de voz ou
          transmissão, diga <Grifo>o que apareceu na tela</Grifo> e{" "}
          <Grifo>em que momento</Grifo> — ao entrar na call, ao começar a transmitir, ou no meio
          dela. Cada um aponta para um lugar diferente, e é o que encurta o diagnóstico de horas
          para minutos.
        </p>
      </section>
    </div>
  );
}
